import { useRef, useEffect } from "react";
import { X, Trash2, Sparkles, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useGlobalChat } from "./GlobalChatProvider";
import { ChatMessage } from "./ChatMessage";
import { GlobalChatInput } from "./GlobalChatInput";
import { TypingIndicator } from "./TypingIndicator";

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function getPageTitle(pageContext: string): string {
  const titles: Record<string, string> = {
    dashboard: "Dashboard Help",
    practice: "Practice Guide",
    arena: "Arena & Tournament Guide",
    technique: "Technique Help",
    roadmap: "Learning Path Guide",
    leaderboard: "Leaderboard Info",
    general: "MindForge Help",
  };
  return titles[pageContext] || "MindForge Help";
}

function getWelcomeMessage(pageContext: string): string {
  const messages: Record<string, string> = {
    dashboard:
      "Ask me about your progress, XP, streaks, or what to learn next.",
    practice:
      "Ask me about practice modes, difficulty levels, or improving your skills.",
    arena:
      "Ask me about tournaments, competitive strategies, or leaderboard rankings.",
    technique: "Ask me about specific techniques or learning strategies.",
    roadmap: "Ask me about learning paths, module selection, or your goals.",
    general: "Ask me anything about MindForge or mental math training.",
  };
  return messages[pageContext] || messages.general;
}

// =============================================================================
// COMPONENTS
// =============================================================================

/**
 * Floating chat button that appears on selected pages
 */
export function GlobalChatTrigger() {
  const { isOpen, toggleChat } = useGlobalChat();

  return (
    <button
      onClick={toggleChat}
      className={`
        fixed right-0 top-[42%] -translate-y-1/2 z-50
        flex flex-col items-center gap-1.5
        bg-gradient-to-b from-violet-600 to-indigo-600
        hover:from-violet-700 hover:to-indigo-700
        text-white rounded-l-xl px-2 py-4 shadow-lg
        transition-all duration-200
        ${isOpen ? "opacity-0 pointer-events-none" : "opacity-100"}
      `}
      aria-label="Open Master Kaizen chat"
    >
      <MessageCircle className="h-5 w-5" />
      <span className="text-xs font-medium [writing-mode:vertical-rl] rotate-180 tracking-wide">
        Ask Kaizen
      </span>
    </button>
  );
}

/**
 * Chat panel that slides in from the right
 */
export function GlobalChatPanel() {
  const {
    isOpen,
    closeChat,
    messages,
    isLoading,
    error,
    remainingMessages,
    clearHistory,
    pageContext,
  } = useGlobalChat();

  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const pageTitle = getPageTitle(pageContext);
  const welcomeMessage = getWelcomeMessage(pageContext);

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && closeChat()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col h-full"
      >
        {/* Header */}
        <SheetHeader className="px-4 py-3 border-b flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-white" />
              </div>
              <div>
                <SheetTitle className="text-base">Master Kaizen</SheetTitle>
                <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                  {pageTitle}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={clearHistory}
                  className="h-8 w-8"
                  title="Clear chat"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={closeChat}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </SheetHeader>

        {/* Messages */}
        <ScrollArea ref={scrollRef} className="flex-1 px-4">
          <div className="py-4 space-y-4">
            {/* Welcome message if no messages */}
            {messages.length === 0 && !isLoading && (
              <div className="text-center py-8">
                <div className="h-12 w-12 rounded-full bg-gradient-to-r from-violet-100 to-indigo-100 dark:from-violet-900/30 dark:to-indigo-900/30 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="h-6 w-6 text-violet-600 dark:text-violet-400" />
                </div>
                <h3 className="font-medium mb-2">
                  Welcome, young warrior! 🥋
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  I am Master Kaizen, your sensei on this journey.
                </p>
                <p className="text-sm text-muted-foreground">
                  {welcomeMessage}
                </p>
              </div>
            )}

            {/* Message list */}
            {messages.map((message, index) => (
              <ChatMessage key={index} message={message} />
            ))}

            {/* Loading indicator */}
            {isLoading && <TypingIndicator />}

            {/* Error message */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm p-3 rounded-lg">
                {error}
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input */}
        <div className="px-4 py-3 border-t flex-shrink-0">
          <GlobalChatInput />
          {remainingMessages !== null && (
            <p className="text-xs text-muted-foreground text-center mt-2">
              {remainingMessages} messages remaining today
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Complete global chat widget with trigger and panel
 */
export function GlobalChatWidget() {
  return (
    <>
      <GlobalChatTrigger />
      <GlobalChatPanel />
    </>
  );
}
